// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TySupportStatus } from "@/types";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    status: TySupportStatus;
    /** Called once the move is confirmed. Absent makes the trail a picture rather than a control. */
    onPick?: (status: TySupportStatus) => void;
    disabled?: boolean;
};

/** The whole life of a ticket, in order. There is no fourth state and no way off the path. */
const FLOW = constants.supportStatuses as readonly TySupportStatus[];

/**
 * Where a ticket has got to, as the trail it walks — the same control `clients-web` gives a deal,
 * for the same reason: a chip reports one word, and the thing a queue is read for is the distance
 * left.
 *
 * **Every step is clickable, backwards included, and every move is confirmed.** The dots sit on a
 * tile that is itself the way into the ticket, so a mis-aimed click is one pixel away from the thing
 * somebody meant to press — and the status is what the queue is worked from, so a ticket that moves
 * itself to solved is a ticket nobody sees again. The dialog lives here rather than in the two places
 * that draw a trail, so no caller can forget it.
 */
export default function SupportStepper({ status, onPick, disabled }: Props) {
    const t = useTranslation();
    const addComp = usePopup((state) => state.addComp);
    const at = FLOW.indexOf(status);

    const confirm = (step: TySupportStatus) =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.support.moveStatus,
                message: t.support.confirmStatus.replace("{status}", t.support.statuses[step]),
                confirmLabel: t.support.moveStatus,
                danger: false,
                onConfirm: () => onPick?.(step),
            },
        });

    return (
        <div className="support-stepper">
            {FLOW.map((step, index) => {
                const state = index < at ? "done" : index === at ? "now" : "next";

                return (
                    <button
                        key={step}
                        type="button"
                        className="support-stepper__step"
                        data-state={state}
                        disabled={!onPick || disabled || index === at}
                        onClick={(event) => {
                            // The stepper sits on cards that are themselves the way into the ticket.
                            event.stopPropagation();
                            confirm(step);
                        }}
                    >
                        <span className="support-stepper__dot" aria-hidden="true" />
                        <span className="support-stepper__label">{t.support.statuses[step]}</span>
                    </button>
                );
            })}
        </div>
    );
}
